import { errorMsg } from '../utils/errorMsg';
import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Typography, Box, Button, TextField, CircularProgress, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  MenuItem, Chip, Tabs, Tab, FormControl, InputLabel, Select, Divider
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ArticleIcon from '@mui/icons-material/Article';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ptBR } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import api from '../axios';
import Header from '../components/Header';
import ContextNavBar from '../components/ContextNavBar';
import ModuleHeader from '../components/ModuleHeader';
import Footer from '../components/Footer';

const LIME = '#00F1A7';
const DARK = '#1C1C1E';
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.conectachurch.com.br';

export default function Aulas() {
  const navigate = useNavigate();
  const handleLogout = () => { localStorage.removeItem('token'); navigate('/login'); };

  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [sucesso, setSucesso] = useState('');

  const [openModal, setOpenModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [aulas, setAulas] = useState([]);
  const [dataAula, setDataAula] = useState(null);
  const [licao, setLicao] = useState('');
  const [numeroLicao, setNumeroLicao] = useState('');
  const [tipoAula, setTipoAula] = useState('PRESENCIAL');
  const [linkStreaming, setLinkStreaming] = useState('');
  const [turmas, setTurmas] = useState([]);
  const [licoes, setLicoes] = useState([]);
  const [turmaId, setTurmaId] = useState('');
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState({});
  const [pdfFile, setPdfFile] = useState(null);
  const [editPdfFile, setEditPdfFile] = useState(null);

  const carregar = async () => {
    setLoading(true); setErro('');
    try {
      const [resAulas, resTurmas, resLicoes] = await Promise.all([
        api.get('/aulas/'),
        api.get('/turmas/'),
        api.get('/licoes/')
      ]);
      setAulas(resAulas.data);
      setTurmas(resTurmas.data);
      setLicoes(resLicoes.data);
    } catch (err) {
      setErro(errorMsg(err) || 'Erro ao carregar dados');
    }
    setLoading(false);
  };
  useEffect(() => { carregar(); }, []);

  const formatLocalDateTime = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
  };

  const handleLicaoChange = (e) => {
    const nomeLicao = e.target.value;
    setLicao(nomeLicao);
    const licaoEncontrada = licoes.find(l => l.nome === nomeLicao);
    if (licaoEncontrada) setNumeroLicao(licaoEncontrada.numero);
  };

  const handleEditLicaoChange = (nomeLicao) => {
    setEditData(prev => ({ ...prev, licao: nomeLicao }));
    const licaoEncontrada = licoes.find(l => l.nome === nomeLicao);
    if (licaoEncontrada) setEditData(prev => ({ ...prev, numero_licao: licaoEncontrada.numero }));
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setErro(''); setSucesso('');
    if (!dataAula || !turmaId || !licao || !numeroLicao) {
      setErro('Preencha todos os campos'); return;
    }
    try {
      const formData = new FormData();
      formData.append('data_aula', dataAula ? formatLocalDateTime(dataAula) : '');
      formData.append('turma_id', turmaId);
      formData.append('licao', licao);
      formData.append('numero_licao', numeroLicao);
      formData.append('tipo_aula', tipoAula);
      if (linkStreaming) formData.append('link_streaming', linkStreaming);
      if (pdfFile) formData.append('pdf', pdfFile);
      await api.post('/aulas/', formData);
      setDataAula(null); setTurmaId(''); setLicao(''); setNumeroLicao(''); setTipoAula('PRESENCIAL'); setLinkStreaming(''); setPdfFile(null);
      setSucesso('Aula cadastrada!');
      carregar();
      setOpenModal(false);
    } catch {
      setErro('Erro de conexão');
    }
  };

  const handleDelete = async (id) => {
    setErro('');
    try {
      await api.delete(`/aulas/${id}`);
      setSucesso('Aula excluída!');
      carregar();
    } catch (err) {
      setErro(errorMsg(err) || 'Erro ao excluir');
    }
  };

  const handleDeletePDF = async (aulaId) => {
    if (!window.confirm('Tem certeza que deseja remover o PDF desta aula?')) return;
    try {
      await api.delete(`/aulas/${aulaId}/pdf`);
      setSucesso('PDF removido!');
      carregar();
      if (editId === aulaId) setEditData({...editData, pdf_path: null});
    } catch {
      setErro('Erro ao remover PDF');
    }
  };

  const handleDownloadPDF = (id) => {
    window.open(`${API_BASE_URL}/relatorios/frequencia/aula/${id}`, '_blank');
  };

  const handleEdit = (id) => {
    const aula = aulas.find(a => a.id === id);
    setEditId(id);
    setEditData({
      data: aula.data ? new Date(aula.data) : null,
      turma_id: aula.turma_id,
      licao: aula.licao,
      numero_licao: aula.numero_licao,
      tipo_aula: aula.tipo_aula || 'PRESENCIAL',
      link_streaming: aula.link_streaming || ''
    });
    setEditMode(true);
    setOpenModal(true);
  };

  const handleEditSave = async (id) => {
    setErro(''); setSucesso('');
    try {
      const body = {
        data: editData.data instanceof Date ? formatLocalDateTime(editData.data) : editData.data,
        turma_id: editData.turma_id,
        licao: editData.licao,
        numero_licao: editData.numero_licao,
        tipo_aula: editData.tipo_aula,
        link_streaming: editData.link_streaming || null
      };
      await api.put(`/aulas/${id}`, body);
      if (editPdfFile) {
        const formData = new FormData();
        formData.append('pdf', editPdfFile);
        await api.post(`/aulas/${id}/pdf`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setEditPdfFile(null);
      }
      setEditId(null);
      setEditData({});
      setSucesso('Aula atualizada!');
      carregar();
      setOpenModal(false);
      setEditMode(false);
    } catch {
      setErro('Erro de conexão');
    }
  };

  const handleEditCancel = () => {
    setEditId(null); setEditData({}); setOpenModal(false); setEditMode(false);
  };

  // ===== EDITOR DE CONTEÚDO =====
  const [openConteudo, setOpenConteudo] = useState(false);
  const [aulaConteudo, setAulaConteudo] = useState(null);
  const [loadingConteudo, setLoadingConteudo] = useState(false);
  const [tabConteudo, setTabConteudo] = useState(0);
  const [blocos, setBlocos] = useState([]);
  const [fotoFile, setFotoFile] = useState(null);
  const [fotoPreview, setFotoPreview] = useState(null);
  const [quiz, setQuiz] = useState([]);
  const [novaPergunta, setNovaPergunta] = useState({ pergunta: '', opcao_a: '', opcao_b: '', opcao_c: '', opcao_d: '', resposta_correta: 'a' });
  const [editandoPergunta, setEditandoPergunta] = useState(null);

  const abrirEditorConteudo = useCallback(async (aula) => {
    setAulaConteudo(aula);
    setTabConteudo(0);
    setFotoFile(null);
    setFotoPreview(aula.foto_capa ? `${API_BASE_URL}/aulas/${aula.id}/foto?t=${Date.now()}` : null);
    try {
      const det = await api.get(`/aulas/${aula.id}/detalhe`);
      setBlocos(det.data.blocos || []);
    } catch { setBlocos([]); }
    try {
      const qz = await api.get(`/aulas/${aula.id}/quiz`);
      setQuiz(qz.data || []);
    } catch { setQuiz([]); }
    setNovaPergunta({ pergunta: '', opcao_a: '', opcao_b: '', opcao_c: '', opcao_d: '', resposta_correta: 'a' });
    setEditandoPergunta(null);
    setOpenConteudo(true);
  }, []);

  const adicionarBloco = (tipo) => {
    const novosDefaults = {
      titulo: { tipo: 'titulo', conteudo: '', nivel: 2 },
      texto: { tipo: 'texto', conteudo: '' },
      video: { tipo: 'video', url: '' },
      imagem: { tipo: 'imagem', url: '', legenda: '' },
      tabela: { tipo: 'tabela', cabecalho: ['Coluna 1', 'Coluna 2'], linhas: [['', '']] },
    };
    setBlocos(prev => [...prev, novosDefaults[tipo]]);
  };

  const atualizarBloco = (idx, campo, valor) => {
    setBlocos(prev => prev.map((b, i) => i === idx ? { ...b, [campo]: valor } : b));
  };

  const removerBloco = (idx) => setBlocos(prev => prev.filter((_, i) => i !== idx));

  const moverBloco = (idx, dir) => {
    const novo = [...blocos];
    const alvo = idx + dir;
    if (alvo < 0 || alvo >= novo.length) return;
    [novo[idx], novo[alvo]] = [novo[alvo], novo[idx]];
    setBlocos(novo);
  };

  const salvarConteudo = async () => {
    setLoadingConteudo(true);
    try {
      await api.put(`/aulas/${aulaConteudo.id}/conteudo`, { blocos });
      if (fotoFile) {
        const fd = new FormData();
        fd.append('foto', fotoFile);
        await api.post(`/aulas/${aulaConteudo.id}/foto`, fd);
      }
      setSucesso('Conteúdo salvo!');
      carregar();
    } catch { setErro('Erro ao salvar conteúdo'); }
    setLoadingConteudo(false);
  };

  const salvarPergunta = async () => {
    if (!novaPergunta.pergunta || !novaPergunta.opcao_a || !novaPergunta.opcao_b || !novaPergunta.opcao_c || !novaPergunta.opcao_d) {
      setErro('Preencha todos os campos da pergunta'); return;
    }
    try {
      if (editandoPergunta) {
        await api.put(`/quiz/perguntas/${editandoPergunta}`, novaPergunta);
        setEditandoPergunta(null);
      } else {
        await api.post(`/aulas/${aulaConteudo.id}/quiz`, novaPergunta);
      }
      const qz = await api.get(`/aulas/${aulaConteudo.id}/quiz`);
      setQuiz(qz.data);
      setNovaPergunta({ pergunta: '', opcao_a: '', opcao_b: '', opcao_c: '', opcao_d: '', resposta_correta: 'a' });
      setSucesso('Pergunta salva!');
    } catch (e) {
      setErro(e.response?.data?.detail || 'Erro ao salvar pergunta');
    }
  };

  const editarPergunta = (p) => {
    setEditandoPergunta(p.id);
    setNovaPergunta({ pergunta: p.pergunta, opcao_a: p.opcao_a, opcao_b: p.opcao_b, opcao_c: p.opcao_c, opcao_d: p.opcao_d, resposta_correta: p.resposta_correta });
  };

  const deletarPergunta = async (id) => {
    await api.delete(`/quiz/perguntas/${id}`);
    const qz = await api.get(`/aulas/${aulaConteudo.id}/quiz`);
    setQuiz(qz.data);
  };

  if (loading) return <><Header onLogout={handleLogout} /><ContextNavBar context="startfy" /><Container sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Container><Footer /></>;
  if (erro && !openModal && !openConteudo) return <><Header onLogout={handleLogout} /><ContextNavBar context="startfy" /><Container sx={{ py: 4 }}><Alert severity="error">{erro}</Alert></Container><Footer /></>;

  return (
    <>
      <Header onLogout={handleLogout} />
      <ContextNavBar context="startfy" />
      <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 }, px: { xs: 1, md: 2 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: { xs: 1, md: 2 } }}>
          <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: '1.2rem', md: '1.5rem' } }}>Aulas</Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setOpenModal(true); setEditMode(false); setPdfFile(null); setEditPdfFile(null); }}
            sx={{ bgcolor: LIME, color: DARK, '&:hover': { filter: 'brightness(0.9)' } }}>
            Novo
          </Button>
        </Box>

        {sucesso && <Alert severity="success" sx={{ mb: 2 }}>{sucesso}</Alert>}
        {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}

        <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflowX: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Lição</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Nº</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Tipo</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Data</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Turma</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>PDF</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }} align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {aulas.map((aula) => (
                <TableRow key={aula.id} hover sx={{ '&:last-child td': { border: 0 } }}>
                  <TableCell>{aula.licao || '-'}</TableCell>
                  <TableCell>{aula.numero_licao || '-'}</TableCell>
                  <TableCell>
                    <Chip label={aula.tipo_aula} size="small" color={aula.tipo_aula === 'PRESENCIAL' ? 'success' : 'info'} />
                  </TableCell>
                  <TableCell>
                    {aula.data ? new Date(aula.data).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                  </TableCell>
                  <TableCell>{aula.turma_nome || '-'}</TableCell>
                  <TableCell>
                    {aula.pdf_path ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <a href={`${API_BASE_URL}/aulas/${aula.id}/pdf`} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2', textDecoration: 'none', fontSize: '0.8rem' }}>
                          <AttachFileIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.3 }} />Ver PDF
                        </a>
                        <IconButton size="small" color="error" onClick={() => handleDeletePDF(aula.id)} sx={{ p: 0.3 }}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ) : '-'}
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                      <Tooltip title="Editar">
                        <IconButton color="info" size="small" onClick={() => handleEdit(aula.id)}><EditIcon fontSize="small" /></IconButton>
                      </Tooltip>
                      <Tooltip title="Conteúdo">
                        <IconButton color="success" size="small" onClick={() => abrirEditorConteudo(aula)}><ArticleIcon fontSize="small" /></IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir">
                        <IconButton color="error" size="small" onClick={() => handleDelete(aula.id)}><DeleteIcon fontSize="small" /></IconButton>
                      </Tooltip>
                      <Tooltip title="Relatório de frequência">
                        <IconButton color="primary" size="small" onClick={() => handleDownloadPDF(aula.id)}><SaveIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {aulas.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    Nenhuma aula cadastrada.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Dialog Criar/Editar Aula */}
        <Dialog open={openModal} onClose={handleEditCancel} maxWidth="md" fullWidth>
          <DialogTitle sx={{ fontWeight: 700 }}>{editMode ? 'Editar Aula' : 'Cadastrar Aula'}</DialogTitle>
          <DialogContent dividers>
            <Box component="form" onSubmit={editMode ? (e) => { e.preventDefault(); handleEditSave(editId); } : handleAdd} sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
                <DateTimePicker
                  label="Data da Aula"
                  value={editMode ? editData.data : dataAula}
                  onChange={val => editMode ? setEditData(prev => ({ ...prev, data: val })) : setDataAula(val)}
                  renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                />
              </LocalizationProvider>
              <TextField select label="Lição" value={editMode ? editData.licao : licao} onChange={e => editMode ? handleEditLicaoChange(e.target.value) : handleLicaoChange(e)} fullWidth size="small">
                {licoes.map(lic => <MenuItem key={lic.numero} value={lic.nome}>{lic.numero}. {lic.nome}</MenuItem>)}
              </TextField>
              <TextField label="Número da Lição" value={editMode ? editData.numero_licao : numeroLicao} fullWidth size="small" disabled helperText="Preenchido automaticamente ao selecionar a lição" />
              <TextField select label="Tipo de Aula" value={editMode ? editData.tipo_aula : tipoAula} onChange={e => editMode ? setEditData(prev => ({ ...prev, tipo_aula: e.target.value })) : setTipoAula(e.target.value)} fullWidth size="small">
                <MenuItem value="PRESENCIAL">PRESENCIAL</MenuItem>
                <MenuItem value="VIRTUAL">VIRTUAL</MenuItem>
              </TextField>
              <TextField label="Link da Aula Online (Zoom, Meet, etc)" value={editMode ? editData.link_streaming || '' : linkStreaming} onChange={e => editMode ? setEditData(prev => ({ ...prev, link_streaming: e.target.value })) : setLinkStreaming(e.target.value)} fullWidth size="small" placeholder="https://zoom.us/j/..." helperText="Opcional: Link para aula virtual/streaming" />
              <TextField select label="Turma" value={editMode ? editData.turma_id : turmaId} onChange={e => editMode ? setEditData(prev => ({ ...prev, turma_id: e.target.value })) : setTurmaId(e.target.value)} fullWidth size="small">
                {turmas.map(turma => <MenuItem key={turma.id} value={turma.id}>{turma.nome}</MenuItem>)}
              </TextField>
              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>PDF da Lição/Aula:</Typography>
                <input type="file" accept="application/pdf" onChange={e => { if (!editMode) setPdfFile(e.target.files[0] || null); else setEditPdfFile(e.target.files[0] || null); }} />
                {editMode && editData.pdf_path && !editPdfFile && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <AttachFileIcon color="action" fontSize="small" />
                    <a href={`${API_BASE_URL}/aulas/${editId}/pdf`} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2', textDecoration: 'none', fontSize: '0.875rem' }}>Ver PDF</a>
                    <IconButton size="small" color="error" onClick={() => handleDeletePDF(editId)}><DeleteIcon fontSize="small" /></IconButton>
                  </Box>
                )}
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleEditCancel} startIcon={<CloseIcon />}>Cancelar</Button>
            <Button type="submit" variant="contained" startIcon={<SaveIcon />}
              onClick={editMode ? () => handleEditSave(editId) : handleAdd}
              sx={{ bgcolor: LIME, color: DARK, '&:hover': { filter: 'brightness(0.9)' } }}>
              {editMode ? 'Salvar' : 'Cadastrar'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog Editor de Conteúdo */}
        <Dialog open={openConteudo} onClose={() => setOpenConteudo(false)} maxWidth="md" fullWidth>
          <DialogTitle sx={{ fontWeight: 700 }}>Conteúdo da Aula: {aulaConteudo?.licao}</DialogTitle>
          <DialogContent dividers>
            <Tabs value={tabConteudo} onChange={(_, v) => setTabConteudo(v)} sx={{ mb: 2 }}>
              <Tab label="Foto de Capa" />
              <Tab label="Conteúdo" />
              <Tab label="Quiz" />
            </Tabs>

            {tabConteudo === 0 && (
              <Box>
                {fotoPreview && (
                  <Box sx={{ mb: 2, borderRadius: 2, overflow: 'hidden', maxHeight: 260 }}>
                    <img src={fotoPreview} alt="Capa" style={{ width: '100%', objectFit: 'cover', maxHeight: 260 }} />
                  </Box>
                )}
                <Typography variant="body2" sx={{ mb: 1 }}>Selecione a foto de capa (JPG, PNG):</Typography>
                <input type="file" accept="image/*" onChange={e => {
                  const f = e.target.files[0];
                  if (f) { setFotoFile(f); setFotoPreview(URL.createObjectURL(f)); }
                }} />
                {!fotoPreview && <Typography color="text.secondary" sx={{ mt: 2, fontStyle: 'italic' }}>Nenhuma foto de capa definida.</Typography>}
              </Box>
            )}

            {tabConteudo === 1 && (
              <Box>
                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                  {['titulo', 'texto', 'video', 'imagem', 'tabela'].map(tipo => (
                    <Button key={tipo} size="small" variant="outlined" onClick={() => adicionarBloco(tipo)}>
                      + {tipo.charAt(0).toUpperCase() + tipo.slice(1)}
                    </Button>
                  ))}
                </Box>
                {blocos.length === 0 && <Typography color="text.secondary" sx={{ fontStyle: 'italic', mb: 2 }}>Nenhum bloco. Adicione acima.</Typography>}
                {blocos.map((bloco, idx) => (
                  <Paper key={idx} sx={{ p: 2, mb: 2, borderRadius: 2, border: '1px solid #e0e0e0' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Chip label={bloco.tipo} size="small" color="primary" variant="outlined" />
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton size="small" onClick={() => moverBloco(idx, -1)} disabled={idx === 0}>↑</IconButton>
                        <IconButton size="small" onClick={() => moverBloco(idx, 1)} disabled={idx === blocos.length - 1}>↓</IconButton>
                        <IconButton size="small" color="error" onClick={() => removerBloco(idx)}><DeleteIcon fontSize="small" /></IconButton>
                      </Box>
                    </Box>
                    {bloco.tipo === 'titulo' && (
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField select label="Nível" value={bloco.nivel || 2} onChange={e => atualizarBloco(idx, 'nivel', Number(e.target.value))} size="small" sx={{ width: 90 }}>
                          <MenuItem value={2}>H2</MenuItem>
                          <MenuItem value={3}>H3</MenuItem>
                        </TextField>
                        <TextField fullWidth size="small" label="Título" value={bloco.conteudo || ''} onChange={e => atualizarBloco(idx, 'conteudo', e.target.value)} />
                      </Box>
                    )}
                    {bloco.tipo === 'texto' && (
                      <TextField fullWidth multiline rows={3} size="small" label="Texto" value={bloco.conteudo || ''} onChange={e => atualizarBloco(idx, 'conteudo', e.target.value)} />
                    )}
                    {bloco.tipo === 'video' && (
                      <TextField fullWidth size="small" label="URL do Vídeo (YouTube, etc)" value={bloco.url || ''} onChange={e => atualizarBloco(idx, 'url', e.target.value)} />
                    )}
                    {bloco.tipo === 'imagem' && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <TextField fullWidth size="small" label="URL da Imagem" value={bloco.url || ''} onChange={e => atualizarBloco(idx, 'url', e.target.value)} />
                        <TextField fullWidth size="small" label="Legenda (opcional)" value={bloco.legenda || ''} onChange={e => atualizarBloco(idx, 'legenda', e.target.value)} />
                      </Box>
                    )}
                    {bloco.tipo === 'tabela' && (
                      <Box>
                        <TextField fullWidth size="small" label="Cabeçalhos (separados por |)" value={(bloco.cabecalho || []).join(' | ')} onChange={e => atualizarBloco(idx, 'cabecalho', e.target.value.split('|').map(s => s.trim()))} sx={{ mb: 1 }} />
                        {(bloco.linhas || []).map((linha, li) => (
                          <Box key={li} sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
                            {linha.map((cel, ci) => (
                              <TextField key={ci} size="small" value={cel} label={`L${li+1}C${ci+1}`} onChange={e => {
                                const novasLinhas = bloco.linhas.map((l, ll) => ll === li ? l.map((c, cc) => cc === ci ? e.target.value : c) : l);
                                atualizarBloco(idx, 'linhas', novasLinhas);
                              }} />
                            ))}
                            <IconButton size="small" color="error" onClick={() => atualizarBloco(idx, 'linhas', bloco.linhas.filter((_, ll) => ll !== li))}><DeleteIcon fontSize="small" /></IconButton>
                          </Box>
                        ))}
                        <Button size="small" onClick={() => atualizarBloco(idx, 'linhas', [...(bloco.linhas || []), Array(bloco.cabecalho?.length || 2).fill('')])}>+ Linha</Button>
                      </Box>
                    )}
                  </Paper>
                ))}
              </Box>
            )}

            {tabConteudo === 2 && (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Máximo 5 perguntas por aula.</Typography>
                {quiz.map((p, idx) => (
                  <Paper key={p.id} sx={{ p: 2, mb: 2, borderRadius: 2, border: '1px solid #e0e0e0' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography fontWeight={600}>{idx + 1}. {p.pergunta}</Typography>
                        {['a', 'b', 'c', 'd'].map(op => (
                          <Typography key={op} variant="body2" sx={{ color: p.resposta_correta === op ? 'success.main' : 'text.secondary', fontWeight: p.resposta_correta === op ? 700 : 400 }}>
                            {op.toUpperCase()}) {p[`opcao_${op}`]} {p.resposta_correta === op ? '✓' : ''}
                          </Typography>
                        ))}
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton size="small" color="info" onClick={() => editarPergunta(p)}><EditIcon fontSize="small" /></IconButton>
                        <IconButton size="small" color="error" onClick={() => deletarPergunta(p.id)}><DeleteIcon fontSize="small" /></IconButton>
                      </Box>
                    </Box>
                  </Paper>
                ))}
                {quiz.length < 5 && (
                  <Paper sx={{ p: 2, borderRadius: 2, border: '1px dashed #90caf9' }}>
                    <Typography variant="subtitle2" sx={{ mb: 1.5 }}>{editandoPergunta ? 'Editar Pergunta' : 'Nova Pergunta'}</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      <TextField fullWidth size="small" label="Pergunta" value={novaPergunta.pergunta} onChange={e => setNovaPergunta(p => ({ ...p, pergunta: e.target.value }))} />
                      {['a', 'b', 'c', 'd'].map(op => (
                        <TextField key={op} size="small" label={`Opção ${op.toUpperCase()}`} value={novaPergunta[`opcao_${op}`]} onChange={e => setNovaPergunta(p => ({ ...p, [`opcao_${op}`]: e.target.value }))} />
                      ))}
                      <FormControl size="small">
                        <InputLabel>Resposta Correta</InputLabel>
                        <Select value={novaPergunta.resposta_correta} label="Resposta Correta" onChange={e => setNovaPergunta(p => ({ ...p, resposta_correta: e.target.value }))}>
                          {['a', 'b', 'c', 'd'].map(op => <MenuItem key={op} value={op}>{op.toUpperCase()}</MenuItem>)}
                        </Select>
                      </FormControl>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button variant="contained" size="small" onClick={salvarPergunta} startIcon={<SaveIcon />}
                          sx={{ bgcolor: LIME, color: DARK, '&:hover': { filter: 'brightness(0.9)' } }}>
                          {editandoPergunta ? 'Atualizar' : 'Adicionar'}
                        </Button>
                        {editandoPergunta && <Button size="small" onClick={() => { setEditandoPergunta(null); setNovaPergunta({ pergunta: '', opcao_a: '', opcao_b: '', opcao_c: '', opcao_d: '', resposta_correta: 'a' }); }}>Cancelar</Button>}
                      </Box>
                    </Box>
                  </Paper>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenConteudo(false)} startIcon={<CloseIcon />}>Fechar</Button>
            {(tabConteudo === 0 || tabConteudo === 1) && (
              <Button variant="contained" startIcon={<SaveIcon />} onClick={salvarConteudo} disabled={loadingConteudo}
                sx={{ bgcolor: LIME, color: DARK, '&:hover': { filter: 'brightness(0.9)' } }}>
                {loadingConteudo ? 'Salvando...' : 'Salvar Conteúdo'}
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </Container>
      <Footer />
    </>
  );
}
